import React, { useState, useEffect, useCallback } from 'react';
import { userAPI } from '../../services/api';
import Loading from '../../components/common/Loading';
import { format } from 'date-fns';
import { fr } from 'date-fns/locale';
import { toast } from 'react-toastify';

// Helpers images
import normalizeImagePath, {
  isValidImageUrl,
  getPlaceholderImage,
} from '../../utils/imageUtils';

const Orders = () => {
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedOrder, setSelectedOrder] = useState(null);

  const loadOrders = useCallback(async () => {
    setLoading(true);
    try {
      const res = await userAPI.getOrders();
      const payload = res?.data?.data ?? res?.data ?? [];
      setOrders(Array.isArray(payload) ? payload : []);
    } catch (error) {
      console.error('Erreur chargement commandes:', error);
      toast.error('Impossible de charger les commandes.');
      setOrders([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadOrders();
  }, [loadOrders]);

  const getStatusBadge = (status) => {
    const map = {
      pending: ['En attente', 'bg-yellow-100 text-yellow-800'],
      confirmed: ['Confirmée', 'bg-blue-100 text-blue-800'],
      delivered: ['Livrée', 'bg-green-100 text-green-800'],
      cancelled: ['Annulée', 'bg-red-100 text-red-800'],
      refunded: ['Remboursée', 'bg-purple-100 text-purple-800'],
    };

    const [label, cls] = map[status] || [
      status || 'Inconnu',
      'bg-gray-100 text-gray-800',
    ];

    return (
      <span className={`px-3 py-1 rounded-full text-sm font-semibold ${cls}`}>
        {label}
      </span>
    );
  };

  if (loading) return <Loading />;

  if (!orders.length) {
    return (
      <div className="bg-white rounded-lg shadow p-12 text-center">
        <p className="text-gray-600 text-lg">
          Vous n&apos;avez pas encore de commandes
        </p>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <h2 className="text-2xl font-bold">Mes commandes</h2>

      {orders.map((order) => {
        const createdAt = order.createdAt || order.created;
        let formattedDate = '';
        try {
          formattedDate = createdAt
            ? format(new Date(createdAt), 'dd MMMM yyyy', { locale: fr })
            : '';
        } catch {
          formattedDate = createdAt
            ? new Date(createdAt).toLocaleDateString('fr-FR')
            : '';
        }

        return (
          <div
            key={order._id || order.orderNumber}
            className="bg-white rounded-lg shadow p-6"
          >
            <div className="flex justify-between items-start mb-4">
              <div>
                <h3 className="font-bold text-lg">
                  Commande #
                  {order.orderNumber || String(order._id).slice(-6)}
                </h3>
                <p className="text-gray-600 text-sm">{formattedDate}</p>
              </div>
              {getStatusBadge(order.status || order.paymentStatus)}
            </div>

            <div className="space-y-3 mb-4">
              {(order.items || []).map((item, idx) => {
                const rawImg =
                  item.dealId?.images?.[0] ||
                  item.deal?.images?.[0] ||
                  item.image ||
                  '';

                const normalized = normalizeImagePath(rawImg);
                const imgSrc = isValidImageUrl(normalized)
                  ? normalized
                  : getPlaceholderImage(80, 80, 'Deal');

                const title =
                  item.title ||
                  item.dealId?.title ||
                  item.deal?.title ||
                  'Article';

                const qty = item.quantity || 1;
                const price = (item.price || 0) * qty;

                return (
                  <div
                    key={idx}
                    className="flex items-center space-x-4"
                  >
                    <img
                      src={imgSrc}
                      alt={title}
                      className="w-16 h-16 object-cover rounded"
                      onError={(e) => {
                        e.currentTarget.src = getPlaceholderImage(
                          80,
                          80,
                          'Deal'
                        );
                      }}
                    />
                    <div className="flex-1">
                      <h4 className="font-semibold line-clamp-2">
                        {title}
                      </h4>
                      <p className="text-sm text-gray-600">
                        Quantité : {qty}
                      </p>
                    </div>
                    <div className="font-bold">{price} MAD</div>
                  </div>
                );
              })}
            </div>

            <div className="border-t pt-4 flex justify-between items-center">
              <div>
                <p className="text-sm text-gray-600">Total</p>
                <p className="font-bold text-xl text-primary-600">
                  {order.total || 0} MAD
                </p>
              </div>
              <button
                onClick={() => setSelectedOrder(order)}
                className="text-primary-600 font-semibold hover:underline"
              >
                Voir les détails
              </button>
            </div>
          </div>
        );
      })}

      {/* Modal détails */}
      {selectedOrder && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-40 p-4">
          <div className="bg-white rounded-lg max-w-2xl w-full overflow-y-auto">
            <div className="p-6">
              <div className="flex justify-between mb-4">
                <h3 className="text-xl font-semibold">
                  Détails de la commande
                </h3>
                <button onClick={() => setSelectedOrder(null)}>✕</button>
              </div>

              <div className="space-y-3">
                <p>
                  <strong>Commande :</strong>{' '}
                  {selectedOrder.orderNumber}
                </p>
                <p>
                  <strong>Statut :</strong>{' '}
                  {selectedOrder.status ||
                    selectedOrder.paymentStatus}
                </p>

                <div className="border-t pt-4">
                  <h4 className="font-semibold mb-2">Articles</h4>
                  {(selectedOrder.items || []).map((it, i) => (
                    <div
                      key={i}
                      className="flex justify-between text-sm"
                    >
                      <span>
                        {it.title ||
                          it.dealId?.title ||
                          'Article'}{' '}
                        × {it.quantity || 1}
                      </span>
                      <span className="font-semibold">
                        {(it.price || 0) *
                          (it.quantity || 1)}{' '}
                        MAD
                      </span>
                    </div>
                  ))}
                </div>

                <div className="border-t pt-4 flex justify-between">
                  <span className="font-bold">Total payé</span>
                  <span className="font-bold text-primary-600">
                    {selectedOrder.total} MAD
                  </span>
                </div>

                <div className="text-right">
                  <button
                    onClick={() => setSelectedOrder(null)}
                    className="px-4 py-2 bg-primary-600 text-white rounded"
                  >
                    Fermer
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Orders;
